'use no memo'

import { Box, Button } from '@mui/material'
import { FormFileInput } from '../form/FormFileInput'

import { useTranslator } from '../../hooks/useTranslator'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  getUserImportSchema,
  useEmployeeImportMutation,
  useDownloadSampleImportEmployee,
  IImportResponse,
  IImportError,
  IImportErrorDelete
} from '@/@data'
import { useNotification } from '@/hooks'
import CompanySelection from '../selection/CompanySelection'
import { InvalidEmployeeImportDialog } from './modal/InvalidEmployeeImportDialog'
import { FormInput } from '../form/FormInput'

const ImportEmployeeFileForm = ({
  onClose,
  refetch,
  companyId,
  businessNumber
}: {
  onClose: () => void
  refetch: () => void
  companyId?: number
  businessNumber?: string
}) => {
  const { __ } = useTranslator()
  const { mutate: importEmployee } = useEmployeeImportMutation()
  const { mutate: downloadSampleImportEmployee } = useDownloadSampleImportEmployee()
  const [open, setOpen] = useState(false)
  const [err, setErr] = useState<IImportError[]>()

  const [errDelete, setErrDelete] = useState<IImportErrorDelete[]>()
  const n = useNotification()

  const methods = useForm<any>({
    defaultValues: {
      company_id: '',
      business_number: businessNumber || '',
      file: null
    },
    resolver: yupResolver(getUserImportSchema(__)),
    mode: 'onChange'
  })

  const handleFileReset = () => {
    methods.setValue('file', null)
    // Force form to re-validate and update
    methods.trigger('file')
  }

  const onSubmit = (data: any) => {
    const formData = new FormData()
    const file = data.file[0]

    formData.append('company_id', data.company_id)
    formData.append('file', file)

    importEmployee(
      { data: formData },
      {
        onSuccess: ({ data }: { data: IImportResponse }) => {
          if (data.errorCount && data.errorCount > 0) {
            n.warning(
              `${__('Import company with')} ${data.successCount} ${__('success and')} ${data.errorCount} ${__('failed')}`
            )
            setErr(data.errors)
            setErrDelete(data.dataDelete)

            setOpen(true)
          } else {
            n.success(__('Import company successfully'))
            onClose()
          }

          refetch()
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box className='flex flex-col gap-6'>
            <Controller
              name='company_id'
              control={methods.control}
              render={({ field, fieldState }) => (
                <CompanySelection
                  {...field}
                  fullWidth
                  required
                  label={__('Employer')}
                  onClear={() => {
                    methods.setValue('company_id', 0)
                    methods.setValue('business_number', '')
                  }}
                  onChange={(value, selected) => {
                    field.onChange(value)

                    if (selected) {
                      methods.setValue('business_number', selected.business_number)
                    }
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={!!companyId}
                />
              )}
            />

            <FormInput
              name='business_number'
              label={__('Business Number')}
              placeholder={__('Enter Business Number')}
              fullWidth
              control={methods.control}
              required
              disabled
            />

            <FormFileInput
              name='file'
              label={__('File')}
              placeholder={__('Import File')}
              accept='.xlsx, .xls, .csv'
              required
              fullWidth
              control={methods.control}
              onClear={handleFileReset}
            />
            <div className='flex flex-row gap-4'>
              <Button
                variant='contained'
                className='text-white text-[15px] font-medium leading-[22px] bg-[#3551F2] shadow-none rounded-full w-full'
                type='submit'
                disabled={!methods.formState.isValid}
              >
                {__('Submit')}
              </Button>
              <Button
                variant='contained'
                className='text-[#FF4C51] text-[15px] font-medium leading-[22px] bg-[#FCE2E3] shadow-none rounded-full w-full'
                onClick={onClose}
              >
                {__('Cancel')}
              </Button>
            </div>

            <Button
              variant='contained'
              startIcon={<i className='tabler-download' />}
              className='text-[#ffffff  ] text-[15px] font-medium leading-[22px] bg-[#00BAD1] shadow-none rounded-full w-full'
              onClick={() => {
                const cid = methods.getValues('company_id')

                if (!cid) {
                  n.warning(__('Please select a company first before downloading the template'))

                  return
                }

                downloadSampleImportEmployee(Number(cid))
              }}
            >
              {__('Download Template')}
            </Button>
          </Box>
        </form>
      </FormProvider>
      <InvalidEmployeeImportDialog
        open={open}
        data={err || []}
        dataDelete={errDelete || []}
        onClose={setOpen}
        onFileReset={handleFileReset}
        refetch={refetch}
      />
    </>
  )
}

export default ImportEmployeeFileForm
